import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, conComa } from '../utils/formato';
import { LITROS_POR_TINA, kilosPorTina, rendimientoDeKilos } from '../utils/tina';

// Las variedades de queso.
//
// Cada una con su precio por mayor y su precio al detal, porque son dos
// negocios: al mayorista se le vende barato y mucho, al mostrador caro y poco.
//
// El «rendimiento esperado» —cuántos litros de leche debería costar un kilo—
// no es un dato administrativo: es contra ese número que la pantalla de
// Producción avisa si una cuajada salió mal. Un queso duro necesita más litros
// por kilo que uno blando, así que el número es distinto en cada variedad y
// ponerlos todos iguales hace que la alerta no sirva.

const VACIO = {
  nombre: '', unidad: 'kg', es_leche: null, materia: null,
  precio_mayor: '', precio_detal: '',
  kilos_tina: '', kilos_tina_minimo: '',
  kilos_tina_bufala: '', kilos_tina_bufala_minimo: '',
  // Para el suero: de cada CUÁNTOS litros de suero dulce salen CUÁNTOS de
  // suero. Es como lo dice el quesero —«de 200 salen 15»— y de ahí se saca el
  // rendimiento que guarda el programa.
  suero_de: '', suero_salen: '',
  dias_duracion: 15,
};

// El suero es OTRA materia prima: no sale de la leche que entra a las tinas
// sino del suero dulce que queda al cuajar. Se pregunta en un solo sitio para
// que no haya dos maneras de saberlo.
const esSuero = (p) => !!p && p.materia === 'suero';

// «De 200 litros de suero dulce salen 15» → 13,33 litros de materia prima por
// litro de suero. Es exactamente lo que significa `rendimiento_esperado` para
// el queso —litros de leche por kilo—, así que se guarda en la misma columna.
const rendimientoDelSuero = (de, salen) => {
  const a = parseFloat(String(de).replace(',', '.'));
  const b = parseFloat(String(salen).replace(',', '.'));
  if (!(a > 0) || !(b > 0)) return null;
  return Math.round((a / b) * 100) / 100;
};

// El camino de vuelta, para volver a llenar las dos casillas al abrir la ficha.
// Se guarda una sola cifra, así que el par se reconstruye sobre una base de 200
// litros, que es como lo dice el cliente.
const sueroDelRendimiento = (r) => {
  const n = parseFloat(r);
  if (!(n > 0)) return { de: '', salen: '' };
  return { de: '200', salen: String(Math.round((200 / n) * 100) / 100) };
};

// Nadie piensa en «litros por kilo»: se piensa en «con una cantina de 40
// litros me salen tantos kilos». La pantalla guarda litros por kilo porque es
// lo que se compara contra cada cuajada, pero muestra las dos cosas.
// El tamaño de la tina y la conversión viven en utils/tina.js, que es de donde
// los lee el resto del programa. Aquí había una copia con el 40 escrito otra
// vez: el día que una tina midiera distinto, esta pantalla habría seguido
// diciendo lo de antes sin que nadie lo notara.
const kilosDe = kilosPorTina;
const unKilo = (n) => (Math.round(n * 10) / 10).toString().replace('.', ',');

function Productos({ onCambio }) {
  const [productos, setProductos] = useState([]);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/productos?todos=1').then(r => setProductos(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        ...editando,
        precio_mayor: parseFloat(editando.precio_mayor) || 0,
        precio_detal: parseFloat(editando.precio_detal) || 0,
        es_leche: editando.es_leche || null,
        materia: esSuero(editando) ? 'suero' : null,
        // EL RENDIMIENTO DEL SUERO se pregunta como se habla —«de 200 litros
        // salen 15»— y se guarda en la misma unidad que el del queso: cuántos
        // litros de materia prima cuesta una unidad de producto. 200÷15 = 13,33.
        //
        // Es la misma columna y el mismo significado que el del queso, así que
        // toda la maquinaria de avisos de rendimiento sirve igual sin tocarla.
        //
        // La leche cruda no se hace: no tiene rendimiento que pactar ni contra
        // qué juzgarla. Mandarle uno dejaría avisos de rendimiento sobre algo
        // que nunca pasó por una tina.
        rendimiento_esperado: editando.es_leche ? null
          : (esSuero(editando)
              ? rendimientoDelSuero(editando.suero_de, editando.suero_salen)
              : rendimientoDeKilos(editando.kilos_tina)),
        // El mínimo de kilos es el máximo de litros por kilo: la conversión
        // invierte el sentido, y por eso van cruzados a propósito.
        rendimiento_maximo: esSuero(editando) ? null
          : rendimientoDeKilos(editando.kilos_tina_minimo),
        // Lo mismo pero con búfala. Va aparte y puede quedar vacío: sin dato
        // la cuajada de búfala no se juzga, y eso es mejor que juzgarla contra
        // el número de la vaca —diría «rindió espectacular» siempre—.
        // El suero no se hace con búfala ni con vaca: se hace con suero.
        rendimiento_bufala: esSuero(editando) ? null
          : rendimientoDeKilos(editando.kilos_tina_bufala),
        rendimiento_bufala_maximo: esSuero(editando) ? null
          : rendimientoDeKilos(editando.kilos_tina_bufala_minimo),
        dias_duracion: parseInt(editando.dias_duracion) || 15,
      };
      if (editando.id) await axios.put(`/api/productos/${editando.id}`, datos);
      else await axios.post('/api/productos', datos);
      setAviso({ tipo: 'bueno', texto: `${editando.nombre} quedó guardado` });
      setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const borrar = async (p) => {
    if (!window.confirm(
      `¿Borrar la variedad «${p.nombre}»?\n\n` +
      'Solo se puede si nunca se ha producido ni vendido. Si ya tiene historia, ' +
      'usa «Ocultar» para quitarla de las listas sin perderla.'
    )) return;
    try {
      await axios.delete(`/api/productos/${p.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  const cambiarActivo = async (p) => {
    try {
      await axios.put(`/api/productos/${p.id}`, { activo: p.activo ? 0 : 1 });
      cargar();
    } catch {
      setAviso({ tipo: 'malo', texto: 'No se pudo cambiar' });
    }
  };

  const sinPrecio = productos.filter(p => p.activo && !(p.precio_mayor > 0) && !(p.precio_detal > 0));

  return (
    <>
      <div className="encabezado">
        <h1>Quesos</h1>
        <p>Las variedades que se producen, con su precio y su rendimiento</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {sinPrecio.length > 0 && (
        <div className="aviso ojo">
          <strong>
            {sinPrecio.length === 1
              ? 'Hay una variedad sin precio'
              : `Hay ${sinPrecio.length} variedades sin precio`}
          </strong>{' '}
          — al vender hay que teclear el precio a mano cada vez, y ahí es donde se
          cobra de menos sin darse cuenta. Ponles el precio: {sinPrecio.map(p => p.nombre).join(', ')}.
        </div>
      )}

      <div className="bloque">
        <h2>🧀 Variedades <span className="contador">{productos.length}</span></h2>
        <div className="cuerpo">
          <div className="acciones" style={{ marginBottom: 14 }}>
            <button className="boton" onClick={() => setEditando({ ...VACIO })}>
              ➕ Nueva variedad
            </button>
          </div>

          {productos.length === 0 ? (
            <div className="vacio">
              No hay variedades. Crea las que produces: blando, semiduro, duro, o
              como las llames tú.
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Queso</th>
                    <th style={{ textAlign: 'right' }}>Por mayor</th>
                    <th style={{ textAlign: 'right' }}>Al detal</th>
                    <th style={{ textAlign: 'right' }}>Rinde</th>
                    <th style={{ textAlign: 'right' }}>Dura</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map(p => (
                    <tr key={p.id} style={{ opacity: p.activo ? 1 : 0.45 }}>
                      <td className="recio">
                        {p.nombre}
                        {!p.activo && <span className="pinta gris" style={{ marginLeft: 6 }}>inactivo</span>}
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          por {p.unidad}
                        </div>
                      </td>
                      <td className="num">
                        {p.precio_mayor > 0 ? plata(p.precio_mayor)
                          : <span className="pinta ojo">sin precio</span>}
                      </td>
                      <td className="num">
                        {p.precio_detal > 0 ? plata(p.precio_detal)
                          : <span className="pinta ojo">sin precio</span>}
                      </td>
                      {/* Se decía «40 L dan 4,5 a 5,7 kg», y eso se lee como un
                          rango de lo normal — como si dar 4,5 estuviera bien.
                          No lo es: 5,7 es lo que DEBE dar y 4,5 es apenas el
                          punto donde el programa grita.

                          Carlos lo leyó así y preguntó si 4,5 no era muy bajo.
                          Tenía razón en dudar: con ese tope se le pueden ir 14
                          kilos en un día de doce tinas sin que nada avise. La
                          cifra era la que él puso; lo que estaba mal era cómo
                          la contaba la pantalla. */}
                      <td className="num">
                        {p.rendimiento_esperado > 0 ? (
                          <>
                            <div>{unKilo(kilosDe(p.rendimiento_esperado))} kg por tina</div>
                            {p.rendimiento_maximo > 0 ? (
                              <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--ojo)' }}>
                                avisa por debajo de {unKilo(kilosDe(p.rendimiento_maximo))} kg
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, fontWeight: 400,
                                            color: 'var(--tinta-tenue)' }}>
                                sin aviso puesto
                              </div>
                            )}
                            <div style={{ fontSize: 11, fontWeight: 400,
                                          color: 'var(--tinta-tenue)' }}>
                              {p.rendimiento_esperado} L/kg
                            </div>
                          </>
                        ) : <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}
                      </td>
                      <td className="num">{p.dias_duracion} días</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="boton chico suave"
                                onClick={() => setEditando({
                                  ...VACIO, ...p,
                                  kilos_tina: kilosPorTina(p.rendimiento_esperado) ?? '',
                                  kilos_tina_minimo: kilosPorTina(p.rendimiento_maximo) ?? '',
                                  kilos_tina_bufala: kilosPorTina(p.rendimiento_bufala) ?? '',
                                  kilos_tina_bufala_minimo: kilosPorTina(p.rendimiento_bufala_maximo) ?? '',
                                  ...(p.materia === 'suero'
                                    ? { suero_de: sueroDelRendimiento(p.rendimiento_esperado).de,
                                        suero_salen: sueroDelRendimiento(p.rendimiento_esperado).salen }
                                    : {}),
                                })}>
                          ✏️
                        </button>{' '}
                        <button className="boton chico suave" onClick={() => cambiarActivo(p)}>
                          {p.activo ? 'Ocultar' : 'Activar'}
                        </button>{' '}
                        {/* Borrar de verdad. El servidor se niega si la
                            variedad ya se produjo o se vendió, y dice por qué:
                            esa historia es la prueba de lo que se hizo, no un
                            catálogo. */}
                        <button className="boton chico suave" title="Borrar"
                                onClick={() => borrar(p)}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editando && (
        <div className="bloque">
          <h2>{editando.id ? '✏️ Editar variedad' : '➕ Nueva variedad'}</h2>
          <form onSubmit={guardar}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Nombre *</span>
                  <input value={editando.nombre} required autoFocus
                         placeholder="Queso blando, semiduro, duro…"
                         onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                </label>
                {/* LECHE CRUDA, no queso.
                    
                    Una variedad marcada como leche no se hace: se vende tal
                    cual. No tiene lote —no sale de una tina— y lo que hay para
                    vender es la leche del día que no entró a las tinas.
                    
                    Guarda las dos cosas en una: QUE es leche y CUÁL. La búfala
                    cuesta más y se paga aparte, así que vender «leche» a secas
                    dejaría cobrando la cara al precio de la barata. */}
                <label className="campo">
                  <span>¿Qué es?</span>
                  {/* El suero va en esta MISMA lista y no en una casilla
                      aparte, porque es la misma pregunta: qué es esta
                      variedad. Dos controles para lo mismo dejan marcar
                      «leche» y «suero» a la vez, y de ahí no se vuelve. */}
                  <select value={esSuero(editando) ? 'suero' : (editando.es_leche || '')}
                          onChange={e => {
                            const v = e.target.value;
                            const suero = v === 'suero';
                            setEditando({ ...editando,
                              es_leche: suero ? null : (v || null),
                              materia: suero ? 'suero' : null,
                              unidad: (suero || v) ? 'L'
                                : (editando.unidad === 'L' ? 'kg' : editando.unidad) });
                          }}>
                    <option value="">Queso — se hace en una tina</option>
                    <option value="vaca">🐄 Leche cruda de vaca — se vende tal cual</option>
                    <option value="bufala">🐃 Leche cruda de búfala — se vende tal cual</option>
                    <option value="suero">🥣 Suero — se hace del suero dulce del queso</option>
                  </select>
                </label>
                {!editando.es_leche && !esSuero(editando) && (
                  <label className="campo">
                    <span>Se vende por</span>
                    <select value={editando.unidad}
                            onChange={e => setEditando({ ...editando, unidad: e.target.value })}>
                      <option value="kg">Kilo</option>
                      <option value="pieza">Pieza</option>
                    </select>
                  </label>
                )}
                {editando.es_leche && (
                  <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                    Se vende por <strong>litro</strong> y no lleva rendimiento: no
                    se hace, se despacha. Lo que hay para vender es la leche del
                    día que no entró a las tinas.
                  </div>
                )}
                {esSuero(editando) && (
                  <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                    Se vende por <strong>litro</strong>, al por mayor y al detal.
                    Sale del suero dulce que queda al cuajar el queso, así que{' '}
                    <strong>la materia prima no cuesta</strong>: el costo del
                    litro son los insumos y la mano de obra. Y no cuenta como
                    leche entrada a las tinas ni entra en el rendimiento de las
                    rutas.
                  </div>
                )}
                <label className="campo">
                  <span>Precio por mayor</span>
                  <input type="number" step="1" min="0"
                         value={editando.precio_mayor} placeholder="0"
                         onChange={e => setEditando({ ...editando, precio_mayor: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Precio al detal</span>
                  <input type="number" step="1" min="0"
                         value={editando.precio_detal} placeholder="0"
                         onChange={e => setEditando({ ...editando, precio_detal: e.target.value })} />
                </label>
                {/* Se pregunta en KILOS POR TINA, que es como se habla, y por
                    dentro se guarda en litros por kilo, que es como se calcula.
                    Antes pedía «litros por kilo» y el quesero tenía que dividir
                    40 entre 5,7 de cabeza para poder contestar.

                    OJO CON LA VUELTA: el tope en litros por kilo es un MÁXIMO
                    —más litros por kilo es peor— y en kilos por tina es un
                    MÍNIMO. Por eso la pregunta cambia de forma, no solo de
                    unidad. */}
                {esSuero(editando) && (
                  <>
                    <label className="campo">
                      <span>De cuántos litros de suero dulce</span>
                      <input type="number" step="1" min="0"
                             value={editando.suero_de} placeholder="200"
                             onChange={e => setEditando({ ...editando, suero_de: e.target.value })} />
                    </label>
                    <label className="campo">
                      <span>salen cuántos litros de suero</span>
                      <input type="number" step="0.1" min="0"
                             value={editando.suero_salen} placeholder="15"
                             onChange={e => setEditando({ ...editando, suero_salen: e.target.value })} />
                    </label>
                    {/* Se enseña la cuenta hecha. El quesero dice «de 200 salen
                        15»; el programa guarda 13,33. Sin este renglón, quien
                        abra la ficha mañana no entiende de dónde salió ese
                        número y termina cambiándolo «para arreglarlo». */}
                    {rendimientoDelSuero(editando.suero_de, editando.suero_salen) && (
                      <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                        De cada <strong>{editando.suero_de} L</strong> de suero dulce
                        salen <strong>{editando.suero_salen} L</strong> de suero
                        {' '}— el {conComa(Math.round(
                          (parseFloat(editando.suero_salen) /
                           parseFloat(editando.suero_de)) * 1000) / 10)}%. Contra eso
                        se juzga cada producción.
                      </div>
                    )}
                  </>
                )}
                {!esSuero(editando) && (<>
                <label className="campo">
                  <span>Kilos que da una tina de {LITROS_POR_TINA} litros</span>
                  <input type="number" step="0.1" min="0"
                         value={editando.kilos_tina} placeholder="5,7"
                         onChange={e => setEditando({ ...editando, kilos_tina: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Menos de cuántos kilos ya es problema</span>
                  <input type="number" step="0.1" min="0"
                         value={editando.kilos_tina_minimo} placeholder="5,2"
                         onChange={e => setEditando({ ...editando, kilos_tina_minimo: e.target.value })} />
                </label>
                {/* 🐃 LA BÚFALA, con su propio par de números.
                    La búfala rinde bastante más: la misma tina da más kilos.
                    Con un solo rendimiento, toda cuajada de búfala saldría
                    «rindió excelente» y toda la de vaca «rindió mal», aunque
                    las dos estuvieran normales — y entonces el aviso de
                    rendimiento deja de querer decir nada.

                    Vacío quiere decir que esta variedad no se hace con búfala.
                    No se hereda el número de la vaca. */}
                <label className="campo">
                  <span>🐃 Kilos que da una tina de búfala</span>
                  <input type="number" step="0.1" min="0"
                         value={editando.kilos_tina_bufala}
                         placeholder="Vacío = no se hace con búfala"
                         onChange={e => setEditando({ ...editando, kilos_tina_bufala: e.target.value })} />
                </label>
                <label className="campo">
                  <span>🐃 Menos de cuántos kilos ya es problema</span>
                  <input type="number" step="0.1" min="0"
                         value={editando.kilos_tina_bufala_minimo}
                         onChange={e => setEditando({ ...editando, kilos_tina_bufala_minimo: e.target.value })} />
                </label>
                </>)}
                <label className="campo">
                  <span>Cuántos días dura</span>
                  <input type="number" step="1" min="1"
                         value={editando.dias_duracion}
                         onChange={e => setEditando({ ...editando, dias_duracion: e.target.value })} />
                </label>
              </div>

              {/* En las unidades en que se piensa de verdad */}
              {parseFloat(editando.kilos_tina) > 0 && (
                <div className="aviso" style={{ marginTop: 12 }}>
                  Con <strong>{LITROS_POR_TINA} litros</strong> deberían salir{' '}
                  <strong>{unKilo(parseFloat(editando.kilos_tina))} kilos</strong>
                  {parseFloat(editando.kilos_tina_minimo) > 0 && (
                    <> — si salen menos de{' '}
                      {unKilo(parseFloat(editando.kilos_tina_minimo))}, Producción te avisa.</>
                  )}
                  <div style={{ fontSize: 13, opacity: .75, marginTop: 4 }}>
                    Se guarda como {rendimientoDeKilos(editando.kilos_tina)} litros por kilo.
                  </div>
                  {/* El mínimo tiene que ser MENOR que lo normal. En la unidad
                      vieja —litros por kilo— la comprobación iba al revés, y
                      dejarla igual habría avisado justo cuando estaba bien. */}
                  {parseFloat(editando.kilos_tina_minimo) > 0 &&
                   parseFloat(editando.kilos_tina_minimo) > parseFloat(editando.kilos_tina) && (
                    <div className="aviso ojo" style={{ marginTop: 8, marginBottom: 0 }}>
                      El mínimo está por encima de lo normal. El mínimo es cuántos
                      kilos ya son POCOS, así que tiene que ser el número menor.
                    </div>
                  )}
                </div>
              )}

              {parseFloat(editando.precio_mayor) > 0 && parseFloat(editando.precio_detal) > 0 && (
                <div style={{ marginTop: 12, fontSize: 14 }}>
                  Al detal cobras{' '}
                  <strong className="cifra">
                    {Math.round((editando.precio_detal / editando.precio_mayor - 1) * 100)}%
                  </strong>{' '}
                  más que al mayorista.
                  {parseFloat(editando.precio_detal) < parseFloat(editando.precio_mayor) && (
                    <div className="aviso ojo" style={{ marginTop: 8, marginBottom: 0 }}>
                      El precio al detal está por debajo del de mayoreo. Comprueba que
                      no estén cambiados.
                    </div>
                  )}
                </div>
              )}

              <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 14 }}>
                El rendimiento no es un número exacto sino una <strong>franja</strong>: de
                la receta estándar, 40 litros dan de 5 a 6 kilos de fresco, cerca de 4 de
                semicurado y de 3 a 3,5 de duro. Por eso se ponen dos números, y por eso
                son distintos en cada variedad — con un solo tope para todas, la alerta
                salta en cuajadas buenas y se acaba ignorando.
              </p>

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Productos;
